import { MetricCard } from './metric-card';
import { MetricsDateFilter } from './metrics-date-filter';
import { UseAdvisorMetricsResult } from '../types/metrics-type';

interface OverviewTabProps {
  metrics: UseAdvisorMetricsResult;
  onMetricClick: (tab: string, filterKey: string) => void;
}

export function OverviewTab({ metrics, onMetricClick }: OverviewTabProps) {
  const {
    data: { advisorMetrics, subscriptionMetrics, cancellationMetrics, misc },
    actions: { setDatePreset, setCustomDates },
  } = metrics;
  const {
    totalAdvisors,
    weekActiveAdvisors,
    weekInactiveAdvisors,
    monthActiveAdvisors,
    monthInactiveAdvisors,
    newlyRegisteredAdvisors,
    firstOpenAdvisorsCustomDates,
    lastOpenAdvisorsCustomDates,
  } = advisorMetrics;
  const {
    totalActiveSubscriptions,
    totalInactiveSubscriptions,
    activeSubscriptionTotalSeats,
    totalUtilizedSeats,
    subscriptionUtilizationRate,
  } = subscriptionMetrics;
  const {
    cancellationsTriggeredLast7Days,
    cancellationsTriggeredLast30Days,
    cancellationsTriggeredWithinCustomDates,
  } = cancellationMetrics;
  const { dateFilter } = misc;
  return (
    <div className="space-y-6">
      {/* Advisors Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Advisors</h3>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          <MetricCard
            label="Total"
            value={totalAdvisors}
            description="Total number of advisors registered on the platform"
            onClick={() => onMetricClick('advisors', 'all')}
          />
          <MetricCard
            label="Weekly Active"
            value={weekActiveAdvisors.length}
            description="Advisors who logged in within the last 7 days"
            onClick={() => onMetricClick('advisors', 'weeklyActive')}
          />
          <MetricCard
            label="Weekly Inactive"
            value={weekInactiveAdvisors.length}
            description="Advisors who did not log in within the last 7 days"
            onClick={() => onMetricClick('advisors', 'weeklyInactive')}
          />
          <MetricCard
            label="Monthly Active"
            value={monthActiveAdvisors.length}
            description="Advisors who logged in within the last 30 days"
            onClick={() => onMetricClick('advisors', 'monthlyActive')}
          />
          <MetricCard
            label="Monthly Inactive"
            value={monthInactiveAdvisors.length}
            description="Advisors who did not log in within the last 30 days"
            onClick={() => onMetricClick('advisors', 'monthlyInactive')}
          />
          <MetricCard
            label="Newly Registered"
            value={newlyRegisteredAdvisors.length}
            description="Advisors who registered within the last 7 days"
            onClick={() => onMetricClick('advisors', 'newlyRegistered')}
          />
        </div>
      </div>

      {/* Subscriptions Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Subscriptions</h3>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          <MetricCard
            label="Active"
            value={totalActiveSubscriptions.length}
            onClick={() => onMetricClick('subscriptions', 'active')}
          />
          <MetricCard
            label="Inactive"
            value={totalInactiveSubscriptions.length}
            description="Subscriptions that are not active"
            onClick={() => onMetricClick('subscriptions', 'inactive')}
          />
          <MetricCard
            label="Active Seat Utilization"
            value={`${subscriptionUtilizationRate}%`}
            description={`${totalUtilizedSeats} of ${activeSubscriptionTotalSeats} seats utilized`}
          />
        </div>
      </div>

      {/* Cancellations Section */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Cancellations</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            label="Weekly"
            value={cancellationsTriggeredLast7Days.length}
            description="Cancellations triggered within the last 7 days"
            valueClassName={cancellationsTriggeredLast7Days.length > 5 ? 'text-red-600' : ''}
            onClick={() => onMetricClick('subscriptions', 'cancellationsWeekly')}
          />
          <MetricCard
            label="Monthly"
            value={cancellationsTriggeredLast30Days.length}
            description="Cancellations triggered within the last 30 days"
            valueClassName={cancellationsTriggeredLast30Days.length > 10 ? 'text-red-600' : ''}
            onClick={() => onMetricClick('subscriptions', 'cancellationsMonthly')}
          />
        </div>
      </div>

      <div className="mt-6">
        <MetricsDateFilter
          dateFilter={dateFilter}
          onPresetChange={setDatePreset}
          onCustomDatesChange={setCustomDates}
        />
      </div>

      {/* Additional Metrics */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <MetricCard
          label="New Advisors in Period"
          value={firstOpenAdvisorsCustomDates.length}
          description="Advisors who opened the platform for the first time during the selected period"
          onClick={() => onMetricClick('advisors', 'newInPeriod')}
        />
        <MetricCard
          label="Inactive Advisors in Period"
          value={lastOpenAdvisorsCustomDates.length}
          description="Advisors who left using the platform during the selected period (last open was during this period)"
          onClick={() => onMetricClick('advisors', 'inactiveInPeriod')}
        />
        <MetricCard
          label="Cancellations in Period"
          value={cancellationsTriggeredWithinCustomDates.length}
          description="Cancellations triggered in the selected period"
          valueClassName={cancellationsTriggeredWithinCustomDates.length > 5 ? 'text-red-600' : ''}
          onClick={() => onMetricClick('subscriptions', 'cancellationsInPeriod')}
        />
      </div>
    </div>
  );
}
